import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";

import {
  ArrowLeftRight as SyncAltIcon,
  Banknote as LocalAtmIcon,
  Route as ShapeLine,
  Building2 as BusinessIcon,
  CalendarCheck as EventAvailableIcon,
  CalendarClock as Schedule,
  ChevronDown as ExpandMoreIcon,
  ChevronUp as ExpandLessIcon,
  Code as CodeRoundedIcon,
  FileText as Description,
  GitBranch as AccountTreeOutlinedIcon,
  Grid as GridOn,
  Infinity as AllInclusive,
  Kanban as ViewKanban,
  LayoutDashboard as DashboardOutlinedIcon,
  LayoutDashboard as Dashboard,
  Link2 as SignalCellularConnectedNoInternet4BarIcon,
  List as ListAlt,
  List as ListIcon,
  Megaphone as AnnouncementIcon,
  Megaphone as Campaign,
  MessageCircle as WhatsAppIcon,
  MessageSquare as ForumIcon,
  Paperclip as AttachFile,
  Phone as ContactPhoneOutlinedIcon,
  Settings as SettingsOutlinedIcon,
  Settings2 as SettingsApplications,
  Share2 as DeviceHubOutlined,
  Smartphone as PhonelinkSetup,
  Users as PeopleAltOutlinedIcon,
  Webhook,
  Zap as FlashOnIcon
} from "lucide-react";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";

// 1. DEFINIÇÃO DAS CORES DOS ÍCONES
const flatDark = { color: "#1b1f22", gradient: "linear-gradient(135deg, #88d03e 0%, #63ae18 100%)" };

const iconStyles = {
  dashboard: flatDark,
  tickets: flatDark,
  messages: flatDark,
  kanban: flatDark,
  contacts: flatDark,
  schedules: flatDark,
  tags: flatDark,
  chats: flatDark,
  helps: flatDark,
  campaigns: flatDark,
  flowbuilder: flatDark,
  announcements: flatDark,
  api: flatDark,
  users: flatDark,
  queues: flatDark,
  prompts: flatDark,
  integrations: flatDark,
  connections: flatDark,
  files: flatDark,
  financial: flatDark,
  settings: flatDark,
  companies: flatDark,
  globalConfig: flatDark,
  default: flatDark
};

const useStyles = makeStyles((theme) => ({
  listItem: {
    height: "44px",
    width: "auto",
    transition: "padding 150ms ease",
    "&:hover $iconHoverActive": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  listItemCollapsed: {
    paddingLeft: "8px !important",
    paddingRight: "8px !important",
    justifyContent: "center",
  },
  
  listItemIcon: {
    minWidth: 48,
    justifyContent: "center",
  },

  listItemIconCollapsed: {
    minWidth: 36,
  },

  listItemText: {
    fontSize: "14px",
    color: "#1b1f22",
    whiteSpace: "nowrap",
    overflow: "hidden",
    transition: "opacity 150ms ease",
  },
  avatarActive: {
    backgroundColor: "transparent",
  },
  avatarHover: {
    backgroundColor: "transparent",
  },
  iconHoverActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    height: 36,
    width: 36,
    minWidth: 36,
    minHeight: 36,
    backgroundColor: "transparent",
    transition: "background-color 0.2s ease, transform 0.2s ease",
    "&:hover, &.active": {
      backgroundColor:
        theme.mode === "light"
          ? "rgba(0, 0, 0, 0.04)"
          : "rgba(255, 255, 255, 0.08)",
    },
    "& svg": {
      height: "24px",
      width: "24px",
      minWidth: "24px",
      minHeight: "24px",
      // Ícones mais nítidos no modo claro (sem blur),
      // mantendo o leve glow só no modo escuro
      filter:
        theme.mode === "dark"
          ? "drop-shadow(0 0 1px rgba(0,0,0,0.25))"
          : "none",
    },
  },
  versionChip: {
    background: iconStyles.dashboard.gradient,
    color: "#1b1f22",
    fontWeight: 600,
    fontSize: "0.75rem",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      transform: "scale(1.05)",
    },
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, iconKey, small } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;
  const isCollapsedSubItem = Boolean(small && tooltip);

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const iconStyle = iconStyles[iconKey] || iconStyles.default;

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip
        placement="right"
        arrow
        title={
          <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>
            {primary}
          </Typography>
        }
      >
        {children}
      </Tooltip>
    ) : (
      children
    );

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem
          button
          component={renderLink}
          className={`${classes.listItem} ${
            isCollapsedSubItem ? classes.listItemCollapsed : ""
          }`}
          style={small && !isCollapsedSubItem ? { paddingLeft: "32px" } : {}}
        >
          {icon ? (
            <ListItemIcon
              className={`${classes.listItemIcon} ${
                isCollapsedSubItem ? classes.listItemIconCollapsed : ""
              }`}
            >
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  <Avatar
                    className={`${classes.iconHoverActive} ${
                      isActive ? "active" : ""
                    }`}
                    style={{ color: iconStyle.color }}
                  >
                    {icon}
                  </Avatar>
                </Badge>
              ) : (
                <Avatar
                  className={`${classes.iconHoverActive} ${
                    isActive ? "active" : ""
                  }`}
                  style={{ color: iconStyle.color }}
                >
                  {icon}
                </Avatar>
              )}
            </ListItemIcon>
          ) : null}
          {!tooltip && (
            <ListItemText
              primary={
                <Typography className={classes.listItemText}>
                  {primary}
                </Typography>
              }
            />
          )}
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openHomeSubmenu, setOpenHomeSubmenu] = useState(false);
  const [openConversationsSubmenu, setOpenConversationsSubmenu] = useState(false);
  const [openAutomationsSubmenu, setOpenAutomationsSubmenu] = useState(false);
  const [openAdministrationSubmenu, setOpenAdministrationSubmenu] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);

  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);

  const isManagementActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/moments") ||
    location.pathname.startsWith("/contacts");


  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user?.companyId;
      
      // Only fetch plan configs if user has valid company ID
      if (companyId) {
        try {
          const planConfigs = await getPlanCompany(undefined, companyId);
          setShowCampaigns(planConfigs.plan.useCampaigns);
          setShowKanban(planConfigs.plan.useKanban);
          setShowOpenAi(planConfigs.plan.useOpenAi);
          setShowIntegrations(planConfigs.plan.useIntegrations);
          setShowSchedules(planConfigs.plan.useSchedules);
          setShowInternalChat(planConfigs.plan.useInternalChat);
          setShowExternalApi(planConfigs.plan.useExternalApi);
        } catch (err) {
          // Log error silently - likely a 401 from session invalidation
          console.debug("Failed to fetch plan config:", err.message);
        }
      }
    }
    fetchData();
  }, [user?.companyId, getPlanCompany]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user?.id && socket && Object.keys(socket).length > 0) {
      const companyId = user.companyId;
      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatMainListItems);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatMainListItems);
      };
    }
  }, [socket, user?.id, user?.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div onClick={drawerClose}>
      <Can
        role={
          (user.profile === "user" && user.showDashboard === "enabled") ||
          user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        yes={() => (
          <>
            <Tooltip
              placement="right"
              arrow
              title={
                collapsed ? (
                  <Typography
                    style={{ fontWeight: 700, fontSize: "0.9rem" }}
                  >
                    {i18n.t("mainDrawer.listItems.home")}
                  </Typography>
                ) : (
                  ""
                )
              }
            >
              <ListItem
                dense
                button
                onClick={() => setOpenHomeSubmenu((prev) => !prev)}
              >
                <ListItemIcon>
                  <Avatar
                    className={`${classes.iconHoverActive} ${
                      openHomeSubmenu || isManagementActive
                        ? "active"
                        : ""
                    }`}
                    style={{ color: iconStyles.dashboard.color }}
                  >
                    <DashboardOutlinedIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography className={classes.listItemText}>
                      {i18n.t("mainDrawer.listItems.home")}
                    </Typography>
                  }
                />
                {openHomeSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openHomeSubmenu}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor:
                  theme.mode === "light"
                    ? "rgba(120,120,120,0.1)"
                    : "rgba(120,120,120,0.5)",
              }}
            >
              <List dense component="div" disablePadding>
                <ListItemLink
                  small
                  to="/"
                  primary={i18n.t("mainDrawer.listItems.dashboard")}
                  icon={<Dashboard />}
                  iconKey="dashboard"
                  tooltip={collapsed}
                />
                <ListItemLink
                  small
                  to="/contacts"
                  primary={i18n.t("mainDrawer.listItems.contacts")}
                  icon={<ContactPhoneOutlinedIcon />}
                  iconKey="contacts"
                  tooltip={collapsed}
                />
                <ListItemLink
                  small
                  to="/reports"
                  primary={i18n.t("mainDrawer.listItems.reports")}
                  icon={<Description />}
                  iconKey="dashboard"
                  tooltip={collapsed}
                />
                <Can
                  role={
                    user.profile === "user" &&
                    user.allowRealTime === "enabled"
                      ? "admin"
                      : user.profile
                  }
                  perform={"drawer-admin-items:view"}
                  yes={() => (
                    <ListItemLink
                      small
                      to="/moments"
                      primary={i18n.t(
                        "mainDrawer.listItems.chatsTempoReal"
                      )}
                      icon={<GridOn />}
                      iconKey="dashboard"
                      tooltip={collapsed}
                    />
                  )}
                />
              </List>
            </Collapse>
          </>
        )}
      />

      <Tooltip
        placement="right"
        arrow
        title={
          collapsed ? (
            <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              {i18n.t("mainDrawer.listItems.conversations")}
            </Typography>
          ) : (
            ""
          )
        }
      >
        <ListItem
          dense
          button
          onClick={() => setOpenConversationsSubmenu((prev) => !prev)}
        >
          <ListItemIcon>
            <Avatar
              className={`${classes.iconHoverActive} ${
                openConversationsSubmenu ? "active" : ""
              }`}
              style={{ color: iconStyles.tickets.color }}
            >
              <WhatsAppIcon />
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography className={classes.listItemText}>
                {i18n.t("mainDrawer.listItems.conversations")}
              </Typography>
            }
          />
          {openConversationsSubmenu ? (
            <ExpandLessIcon />
          ) : (
            <ExpandMoreIcon />
          )}
        </ListItem>
      </Tooltip>
      <Collapse
        in={openConversationsSubmenu}
        timeout="auto"
        unmountOnExit
        style={{
          backgroundColor:
            theme.mode === "light"
              ? "rgba(120,120,120,0.1)"
              : "rgba(120,120,120,0.5)",
        }}
      >
        <List dense component="div" disablePadding>
          <ListItemLink
            small
            to="/tickets"
            primary={i18n.t("mainDrawer.listItems.tickets")}
            icon={<WhatsAppIcon />}
            iconKey="tickets"
            tooltip={collapsed}
          />
          <ListItemLink
            small
            to="/quick-messages"
            primary={i18n.t("mainDrawer.listItems.quickMessages")}
            icon={<FlashOnIcon />}
            iconKey="messages"
            tooltip={collapsed}
          />
          {showKanban && (
            <ListItemLink
              small
              to="/kanban"
              primary={i18n.t("mainDrawer.listItems.kanban")}
              icon={<ViewKanban />}
              iconKey="kanban"
              tooltip={collapsed}
            />
          )}
          {showSchedules && (
            <ListItemLink
              small
              to="/schedules"
              primary={i18n.t("mainDrawer.listItems.schedules")}
              icon={<Schedule />}
              iconKey="schedules"
              tooltip={collapsed}
            />
          )}
          {showInternalChat && (
            <ListItemLink
              small
              to="/chats"
              primary={i18n.t("mainDrawer.listItems.chats")}
              icon={
                <Badge
                  color="secondary"
                  variant="dot"
                  invisible={invisible}
                >
                  <ForumIcon />
                </Badge>
              }
              iconKey="chats"
              tooltip={collapsed}
            />
          )}
        </List>
      </Collapse>

      <Can
        role={
          user.profile === "user" &&
          user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
        yes={() => (
          <>
            <Tooltip
              placement="right"
              arrow
              title={
                collapsed ? (
                  <Typography
                    style={{ fontWeight: 700, fontSize: "0.9rem" }}
                  >
                    {i18n.t("mainDrawer.listItems.automations")}
                  </Typography>
                ) : (
                  ""
                )
              }
            >
              <ListItem
                dense
                button
                onClick={() =>
                  setOpenAutomationsSubmenu((prev) => !prev)
                }
              >
                <ListItemIcon>
                  <Avatar
                    className={`${classes.iconHoverActive} ${
                      openAutomationsSubmenu ? "active" : ""
                    }`}
                    style={{ color: iconStyles.flowbuilder.color }}
                  >
                    <FlashOnIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography className={classes.listItemText}>
                      {i18n.t("mainDrawer.listItems.automations")}
                    </Typography>
                  }
                />
                {openAutomationsSubmenu ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openAutomationsSubmenu}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor:
                  theme.mode === "light"
                    ? "rgba(120,120,120,0.1)"
                    : "rgba(120,120,120,0.5)",
              }}
            >
              <List dense component="div" disablePadding>
                {showCampaigns && (
                  <>
                    <ListItemLink
                      small
                      to="/campaigns"
                      primary={i18n.t("campaigns.subMenus.list")}
                      icon={<EventAvailableIcon />}
                      iconKey="campaigns"
                      tooltip={collapsed}
                    />
                  </>
                )}
                <ListItemLink
                  small
                  to="/flowbuilders"
                  primary={i18n.t("flowbuilder.subMenus.conversation")}
                  icon={<ShapeLine />}
                  iconKey="flowbuilder"
                  tooltip={collapsed}
                />
                {showOpenAi && (
                  <ListItemLink
                    small
                    to="/prompts"
                    primary={i18n.t("mainDrawer.listItems.prompts")}
                    icon={<AllInclusive />}
                    iconKey="prompts"
                    tooltip={collapsed}
                  />
                )}
                <ListItemLink
                  small
                  to="/files"
                  primary={i18n.t("mainDrawer.listItems.files")}
                  icon={<AttachFile />}
                  iconKey="files"
                  tooltip={collapsed}
                />
              </List>
            </Collapse>

            <Tooltip
              placement="right"
              arrow
              title={
                collapsed ? (
                  <Typography
                    style={{ fontWeight: 700, fontSize: "0.9rem" }}
                  >
                    {i18n.t("mainDrawer.listItems.administration")}
                  </Typography>
                ) : (
                  ""
                )
              }
            >
              <ListItem
                dense
                button
                onClick={() =>
                  setOpenAdministrationSubmenu((prev) => !prev)
                }
              >
                <ListItemIcon>
                  <Avatar
                    className={`${classes.iconHoverActive} ${
                      openAdministrationSubmenu ? "active" : ""
                    }`}
                    style={{ color: iconStyles.settings.color }}
                  >
                    <SettingsOutlinedIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography className={classes.listItemText}>
                      {i18n.t("mainDrawer.listItems.administration")}
                    </Typography>
                  }
                />
                {openAdministrationSubmenu ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openAdministrationSubmenu}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor:
                  theme.mode === "light"
                    ? "rgba(120,120,120,0.1)"
                    : "rgba(120,120,120,0.5)",
              }}
            >
              <List dense component="div" disablePadding>
                {user.super && (
                  <ListItemLink
                    small
                    to="/announcements"
                    primary={i18n.t(
                      "mainDrawer.listItems.announcements"
                    )}
                    icon={<AnnouncementIcon />}
                    iconKey="announcements"
                    tooltip={collapsed}
                  />
                )}
                {showExternalApi && (
                  <ListItemLink
                    small
                    to="/messages-api"
                    primary={i18n.t(
                      "mainDrawer.listItems.messagesAPI"
                    )}
                    icon={<CodeRoundedIcon />}
                    iconKey="api"
                    tooltip={collapsed}
                  />
                )}
                <ListItemLink
                  small
                  to="/users"
                  primary={i18n.t("mainDrawer.listItems.users")}
                  icon={<PeopleAltOutlinedIcon />}
                  iconKey="users"
                  tooltip={collapsed}
                />
                <ListItemLink
                  small
                  to="/queues"
                  primary={i18n.t("mainDrawer.listItems.queues")}
                  icon={<AccountTreeOutlinedIcon />}
                  iconKey="queues"
                  tooltip={collapsed}
                />
                {showIntegrations && (
                  <ListItemLink
                    small
                    to="/queue-integration"
                    primary={i18n.t(
                      "mainDrawer.listItems.queueIntegration"
                    )}
                    icon={<DeviceHubOutlined />}
                    iconKey="integrations"
                    tooltip={collapsed}
                  />
                )}
                <ListItemLink
                  small
                  to="/connections"
                  primary={i18n.t(
                    "mainDrawer.listItems.connections"
                  )}
                  icon={<SignalCellularConnectedNoInternet4BarIcon />}
                  iconKey="connections"
                  showBadge={connectionWarning}
                  tooltip={collapsed}
                />
                <ListItemLink
                  small
                  to="/financeiro"
                  primary={i18n.t(
                    "mainDrawer.listItems.financeiro"
                  )}
                  icon={<LocalAtmIcon />}
                  iconKey="financial"
                  tooltip={collapsed}
                />
                <ListItemLink
                  small
                  to="/settings"
                  primary={i18n.t("mainDrawer.listItems.settings")}
                  icon={<SettingsOutlinedIcon />}
                  iconKey="settings"
                  tooltip={collapsed}
                />
                {user.super && (
                  <ListItemLink
                    small
                    to="/global-config"
                    primary={i18n.t(
                      "globalConfig.title",
                      "Configurações Globais"
                    )}
                    icon={<SettingsApplications />}
                    iconKey="globalConfig"
                    tooltip={collapsed}
                  />
                )}
              </List>
            </Collapse>
          </>
        )}
      />
      {!collapsed && (
        <React.Fragment>
          <Divider />
        </React.Fragment>
      )}
    </div>
  );
};

export default MainListItems;
